import {useMutation, UseMutationOptions, useQueryClient, InfiniteData} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpCreateConversation, CreateConversationPayload} from '../http/conversations.http'
import {Conversation, ConversationsList} from '@/features/conversations/types.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'

export const useCreateConversation = (
    options?: UseMutationOptions<Conversation, AxiosError, CreateConversationPayload>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (payload: CreateConversationPayload) =>
            parseAxiosPromise({
                axiosPromise: httpCreateConversation(payload),
                onZodError: captureException,
                responseShape: Conversation
            }),
        onSuccess: (newConversation, variables, context) => {
            queryClient.setQueriesData(
                {
                    queryKey: [QUERY_KEYS.CONVERSATIONS, variables.patientId ?? null],
                    exact: false
                },
                (oldData: InfiniteData<{data: ConversationsList}> | undefined) => {
                    if (!oldData) {
                        return {
                            pages: [
                                {
                                    data: {
                                        data: [newConversation],
                                        meta: {
                                            currentPage: 1,
                                            lastPage: 1,
                                            perPage: 30,
                                            total: 1
                                        }
                                    }
                                }
                            ],
                            pageParams: [1]
                        }
                    }

                    const updatedPages = [...oldData.pages]
                    if (updatedPages.length > 0) {
                        updatedPages[0] = {
                            ...updatedPages[0],
                            data: {
                                ...updatedPages[0].data,
                                data: [newConversation, ...updatedPages[0].data.data],
                                meta: {
                                    ...updatedPages[0].data.meta,
                                    total: updatedPages[0].data.meta.total + 1
                                }
                            }
                        }
                    }

                    return {
                        ...oldData,
                        pages: updatedPages
                    }
                }
            )

            options?.onSuccess?.(newConversation, variables, context)
        },
        onError: options?.onError,
        onMutate: options?.onMutate,
        onSettled: options?.onSettled
    })
}
